/**
 * Bidirectional transform between nested i18n JSON and a flat dot-notation map.
 *
 * The translation editor (O2D-PRD-092) loads nested de.json, flattens it into
 * dot-notation table rows, and unflattens on save. The transform MUST agree with the
 * backend SaveTranslationOverrideCommandHandler (O2D-PRD-090): separator ".", string
 * leaves only, and drop-empty (an empty value removes the override). Forbidden segments
 * (__proto__/constructor/prototype) are dropped as defense-in-depth so unflatten never
 * becomes a prototype-pollution sink; the backend remains the authoritative guard.
 */
export class TranslationFlattenHelper {
    private static readonly SEPARATOR = '.';

    /** Key segments that could pollute the JS consumer's prototype chain. */
    private static readonly FORBIDDEN_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);

    /** Flatten a nested object into a dot-notation Map, keeping string leaves only. */
    public static flatten(nested: Record<string, unknown>): Map<string, string> {
        const flat = new Map<string, string>();
        this.walk(nested, '', flat);

        return flat;
    }

    /** Unflatten a dot-notation map (record or Map) into nested JSON, dropping empty values. */
    public static unflatten(flat: Record<string, string> | Map<string, string>): Record<string, unknown> {
        const entries = flat instanceof Map ? flat.entries() : Object.entries(flat);
        const nested: Record<string, unknown> = {};
        for (const [key, value] of entries) {
            if (value === '' || value === null || value === undefined) {
                continue; // drop-empty: an empty value removes the override (mirrors 090)
            }
            const segments = this.safeSegments(key);
            if (segments !== null) {
                this.setNested(nested, segments, value);
            }
        }

        return nested;
    }

    /** Recursive descent: string leaves are emitted, nested objects recursed, other leaves dropped. */
    private static walk(node: Record<string, unknown>, prefix: string, flat: Map<string, string>): void {
        for (const [key, value] of Object.entries(node)) {
            const path = prefix ? `${prefix}${this.SEPARATOR}${key}` : key;
            if (typeof value === 'string') {
                flat.set(path, value);
            } else if (this.isPlainObject(value)) {
                this.walk(value, path, flat);
            }
        }
    }

    /** Split a key on the separator; null when any segment is empty or forbidden. */
    private static safeSegments(key: string): string[] | null {
        const segments = key.split(this.SEPARATOR);
        const invalid = segments.some(segment => segment === '' || this.FORBIDDEN_SEGMENTS.has(segment));

        return invalid ? null : segments;
    }

    /** Assign a value at the nested path, creating intermediate objects as needed. */
    private static setNested(root: Record<string, unknown>, segments: string[], value: string): void {
        let cursor = root;
        for (let i = 0; i < segments.length - 1; i++) {
            const segment = segments[i];
            if (!this.isPlainObject(cursor[segment])) {
                cursor[segment] = {};
            }
            cursor = cursor[segment] as Record<string, unknown>;
        }
        cursor[segments[segments.length - 1]] = value;
    }

    private static isPlainObject(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null && !Array.isArray(value);
    }
}
